import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Bell, MessageSquare, Coins, Swords, Sparkles, Gem } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TYPE_ICON = { trade: MessageSquare, market: Coins, arena: Swords, card: Gem };

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const lastSeenId = useRef(null);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 12000,
  });

  const items = useMemo(() => data?.items || [], [data?.items]);
  const unread = data?.unread || 0;

  // Toast the freshest notification the first time we see it.
  useEffect(() => {
    if (!items.length) return;
    const newest = items[0];
    if (lastSeenId.current === null) { lastSeenId.current = newest.id; return; }
    if (newest.id !== lastSeenId.current && !newest.read) {
      const Icon = TYPE_ICON[newest.type] || Sparkles;
      toast(newest.title, {
        description: newest.body,
        icon: newest.image
          ? <img src={newest.image} alt="" className="h-9 w-9 rounded object-cover" />
          : <Icon className="h-4 w-4" />,
        action: newest.link ? { label: "Open", onClick: () => navigate(newest.link) } : undefined,
      });
    }
    lastSeenId.current = newest.id;
  }, [items, navigate]);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await api.post("/notifications/read");
        qc.invalidateQueries({ queryKey: ["notifications"] });
      } catch (_) { /* silent */ }
    }
  };

  const go = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="notification-bell"
        onClick={toggle}
        aria-label="Notifications"
        className="relative hud-pill h-9 w-9 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black font-mono text-[10px] font-bold flex items-center justify-center animate-pulse">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-xl border border-white/10 bg-[hsl(222_22%_8%)] shadow-[0_18px_50px_rgba(0,0,0,0.6)] z-50">
          <div className="px-4 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Notifications</div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-white/40 text-sm">You're all caught up.</div>
          ) : (
            items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Sparkles;
              return (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => go(n)}
                  className={cn("w-full text-left px-4 py-3 flex gap-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors", !n.read && "bg-cyan-400/[0.06]")}
                >
                  <div className="h-8 w-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {n.image
                      ? <img src={n.image} alt="" className="h-full w-full object-cover" data-testid={`notification-thumb-${n.id}`} />
                      : <Icon className="h-4 w-4 text-cyan-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm leading-tight">{n.title}</div>
                    {n.body && <div className="text-white/50 text-xs mt-0.5 leading-tight">{n.body}</div>}
                    <div className="font-mono text-[9px] text-white/30 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
